import { Navigate, type RouteObject } from 'react-router';
import { RequireAuth } from '@/app/auth/RequireAuth';
import { RouteErrorBoundary } from '@/features/shared/RouteErrorBoundary';

/*
 * P-32 (D-132): the carrier portal's route tree — mounted at `/carrier` outside `AppLayout`, inside `RequireAuth`; the
 * layout decides between a carrier's own view and a staff preview. Static words rank above `:id`.
 */

const lazy = (load: () => Promise<Record<string, unknown>>, name: string) => () => load().then((m) => ({ Component: m[name] as React.ComponentType }));
const writes = (...routes: RouteObject[]): RouteObject => ({ lazy: () => import('./CarrierLayout').then((m) => ({ Component: m.CarrierWriteGuard })), children: routes });

export const carrierRoutes: RouteObject[] = [
  {
    path: '/carrier',
    lazy: () => import('./CarrierLayout').then((m) => ({ element: <RequireAuth><m.CarrierLayout /></RequireAuth> })),
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, lazy: lazy(() => import('./pages/CarrierOverviewPage'), 'CarrierOverviewPage') },
      {
        path: 'tenders',
        children: [
          { index: true, lazy: lazy(() => import('./pages/CarrierTendersPage'), 'CarrierTendersPage') },
          writes({ path: ':offerId/respond', lazy: lazy(() => import('./pages/CarrierTendersPage'), 'RespondToTenderPage') }, { path: ':offerId/confirm', lazy: lazy(() => import('./pages/CarrierTendersPage'), 'ConfirmAwardPage') }),
        ],
      },
      {
        path: 'loads',
        children: [
          { index: true, lazy: lazy(() => import('./pages/CarrierLoadsPage'), 'CarrierLoadsPage') },
          { path: ':loadId', lazy: lazy(() => import('./pages/CarrierLoadsPage'), 'CarrierLoadPage') },
          writes(
            { path: ':loadId/milestone', lazy: lazy(() => import('./pages/CarrierUpdatePages'), 'ReportMilestonePage') },
            { path: ':loadId/eta', lazy: lazy(() => import('./pages/CarrierUpdatePages'), 'UpdateEtaPage') },
            { path: ':loadId/document', lazy: lazy(() => import('./pages/CarrierUpdatePages'), 'UploadDocumentPage') },
            { path: ':loadId/pod', lazy: lazy(() => import('./pages/CarrierUpdatePages'), 'RecordPodPage') },
          ),
        ],
      },
      {
        path: 'invoices',
        children: [
          { index: true, lazy: lazy(() => import('./pages/CarrierInvoicesPage'), 'CarrierInvoicesPage') },
          writes({ path: 'new', lazy: lazy(() => import('./pages/CarrierInvoicesPage'), 'SubmitInvoicePage') }),
        ],
      },
      {
        path: 'team',
        children: [
          { index: true, lazy: lazy(() => import('./pages/CarrierTeamPage'), 'CarrierTeamPage') },
          writes({ path: 'invite', lazy: lazy(() => import('./pages/CarrierTeamPage'), 'InviteCarrierUserPage') }, { path: ':userId/role', lazy: lazy(() => import('./pages/CarrierTeamPage'), 'ChangeCarrierUserRolePage') }, { path: ':userId/remove', lazy: lazy(() => import('./pages/CarrierTeamPage'), 'RemoveCarrierUserPage') }),
        ],
      },
      { path: '*', element: <Navigate to="/carrier" replace /> },
    ],
  },
];
